import { Particle } from '../ParticleLayer'

// Elvish/Alien Rune Paths (SVG-like commands)
const RUNE_PATHS = [
    // Complex Rune 1
    "M0,-25 C10,-25 15,-15 10,-5 C5,5 -5,5 -10,15 L0,25 M-15,-15 L15,-15 M-10,5 L10,5",
    // Complex Rune 2
    "M-10,-25 L10,-25 L0,-5 L10,15 L-10,15 L0,-5 Z M0,-5 L0,25",
    // Complex Rune 3
    "M0,-25 Q15,-15 0,0 T0,25 M-15,0 L15,0 M0,-15 L10,-20 M0,15 L-10,20",
    // Complex Rune 4
    "M-5,-25 L-5,25 M5,-25 L5,25 M-15,-5 L15,-5 M-15,15 L15,15 M0,-25 L15,-30 M0,25 L-15,30",
    // Complex Rune 5
    "M0,0 m-12,0 a12,12 0 1,0 24,0 a12,12 0 1,0 -24,0 M0,-12 L0,-28 M0,12 L0,28 M-12,0 L-20,0 M12,0 L20,0"
]

export type DispelShard = {
    x: number
    y: number
    z: number // Height from ground
    vx: number
    vy: number
    vz: number // Velocity towards camera (up)
    angle: number
    rotationSpeed: number
    path: Path2D
    color: string
    life: number
    maxLife: number
}

export type DispelSymbol = {
    id: number
    angle: number // Position on the ring
    charIndex: number
    path: Path2D
    size: number
    opacity: number
    phase: number // For floating animation
    shaking: number
    color: string
    glow: number
    broken: boolean
}

export type DispelEffect = {
    kind: 'dispel'
    t: number
    x: number
    y: number
    data: {
        symbols: DispelSymbol[]
        shards: DispelShard[]
        ringRotation: number
        ringRadius: number
        phase: 'gather' | 'tremble' | 'explode'
        flash: number
    }
}

export const createDispelEffect = (x: number, y: number): DispelEffect => {
    const symbols: DispelSymbol[] = []
    const count = 12

    for (let i = 0; i < count; i++) {
        const angle = (i / count) * Math.PI * 2
        const charIndex = Math.floor(Math.random() * RUNE_PATHS.length)

        symbols.push({
            id: i,
            angle: angle,
            charIndex: charIndex,
            path: new Path2D(RUNE_PATHS[charIndex]),
            size: 1.0, // Scale factor
            opacity: 0,
            phase: Math.random() * Math.PI * 2,
            shaking: 0,
            color: 'rgba(75, 0, 130, 1)', // Indigo
            glow: 0,
            broken: false
        })
    }

    return {
        kind: 'dispel',
        t: 0,
        x,
        y,
        data: {
            symbols,
            shards: [],
            ringRotation: 0,
            ringRadius: 100,
            phase: 'gather',
            flash: 0
        }
    }
}

export const renderDispelEffect = (
    effect: DispelEffect,
    ctx: CanvasRenderingContext2D,
    spawnParticle: (p: Particle) => void
): boolean => {
    const { t, x, y, data } = effect
    const maxTime = 4000

    if (t > maxTime) return false

    // Phase Logic
    const trembleStart = 1200
    const explodeStart = 2800

    if (t < trembleStart) {
        data.phase = 'gather'
    } else if (t < explodeStart) {
        data.phase = 'tremble'
    } else {
        data.phase = 'explode'
    }

    ctx.save()
    ctx.translate(x, y)

    // --- Update Global Ring State ---
    if (data.phase === 'gather') {
        data.ringRotation += 0.002
        data.ringRadius = 100 + Math.sin(t * 0.002) * 5
    } else if (data.phase === 'tremble') {
        const progress = (t - trembleStart) / (explodeStart - trembleStart)
        // Rotate faster and faster
        data.ringRotation += 0.005 + progress * 0.15
        // Expand radius at the very end
        if (progress > 0.8) {
            data.ringRadius = 100 + (progress - 0.8) * 500 // Rapid expansion
        } else {
            data.ringRadius = 100 + Math.random() * 2 // Jitter
        }
    }

    // --- Flash Effect ---
    // Trigger flash at the exact moment of explosion
    if (data.phase === 'explode' && t >= explodeStart && t < explodeStart + 100 && data.flash === 0) {
        data.flash = 0.6 // Reduced intensity
    }

    if (data.flash > 0.01) {
        ctx.save()
        ctx.globalCompositeOperation = 'screen' // Softer blend mode

        // Create a radial gradient for a softer, more focused flash
        const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, 800)
        gradient.addColorStop(0, `rgba(255, 255, 255, ${data.flash})`)
        gradient.addColorStop(0.4, `rgba(230, 200, 255, ${data.flash * 0.5})`) // Soft purple/pink tint
        gradient.addColorStop(1, 'rgba(255, 255, 255, 0)')

        ctx.fillStyle = gradient
        ctx.fillRect(-1500, -1500, 3000, 3000)
        ctx.restore()

        data.flash *= 0.9 // Smooth decay
    } else {
        data.flash = 0
    }

    // --- Render Symbols ---
    if (data.phase !== 'explode') {
        data.symbols.forEach((s) => {
            // Logic
            if (data.phase === 'gather') {
                s.opacity = Math.min(1, s.opacity + 0.02)
            } else if (data.phase === 'tremble') {
                const progress = (t - trembleStart) / (explodeStart - trembleStart)
                s.shaking = progress * 15
                s.glow = progress
                // Color Shift: Indigo -> Bright Pink/White
                const r = 75 + (255 - 75) * progress
                const g = 0 + (200 - 0) * progress
                const b = 130 + (255 - 130) * progress
                s.color = `rgba(${Math.floor(r)}, ${Math.floor(g)}, ${Math.floor(b)}, 1)`
            }

            const currentAngle = s.angle + data.ringRotation
            const rx = Math.cos(currentAngle) * data.ringRadius
            const ry = Math.sin(currentAngle) * data.ringRadius

            // Float effect
            const floatY = Math.sin(t * 0.003 + s.phase) * 10

            const shakeX = (Math.random() - 0.5) * s.shaking
            const shakeY = (Math.random() - 0.5) * s.shaking

            const finalX = rx + shakeX
            const finalY = ry + floatY + shakeY

            // Draw Shadow
            ctx.save()
            ctx.translate(finalX, finalY + 40) // Shadow is below
            ctx.scale(1, 0.3)
            ctx.fillStyle = 'rgba(0,0,0,0.4)'
            ctx.globalAlpha = s.opacity * 0.5
            ctx.beginPath()
            ctx.arc(0, 0, 15, 0, Math.PI * 2)
            ctx.fill()
            ctx.restore()

            // Draw Symbol
            ctx.save()
            ctx.translate(finalX, finalY)

            // Glow
            if (s.glow > 0) {
                ctx.shadowColor = 'rgba(255, 200, 255, 0.8)'
                ctx.shadowBlur = s.glow * 30
            }

            ctx.strokeStyle = s.color
            ctx.lineWidth = 3
            ctx.lineCap = 'round'
            ctx.lineJoin = 'round'
            ctx.globalAlpha = s.opacity

            // Scale up slightly based on glow
            const scale = 1.0 + s.glow * 0.2
            ctx.scale(scale, scale)

            ctx.stroke(s.path)
            ctx.restore()
        })
    } else {
        // --- Explode Phase (Shards) ---
        // Initialize shards once
        if (data.shards.length === 0) {
            data.symbols.forEach((s) => {
                const currentAngle = s.angle + data.ringRotation
                const rx = Math.cos(currentAngle) * data.ringRadius
                const ry = Math.sin(currentAngle) * data.ringRadius

                // Break each symbol into multiple shards
                const shardCount = 6
                for (let k = 0; k < shardCount; k++) {
                    // Randomize shard path (just small lines/curves for now as splitting Path2D is hard)
                    // We will simulate shards by drawing small segments
                    const shardPath = new Path2D()
                    const len = 10 + Math.random() * 10
                    shardPath.moveTo(-len / 2, 0)
                    shardPath.lineTo(len / 2, 0)

                    data.shards.push({
                        x: rx,
                        y: ry,
                        z: 0,
                        // Velocity: Outward from center + Random
                        vx: Math.cos(currentAngle) * (10 + Math.random() * 20) + (Math.random() - 0.5) * 10,
                        vy: Math.sin(currentAngle) * (10 + Math.random() * 20) + (Math.random() - 0.5) * 10,
                        vz: 15 + Math.random() * 25, // Blast TOWARDS camera
                        angle: Math.random() * Math.PI * 2,
                        rotationSpeed: (Math.random() - 0.5) * 0.5,
                        path: shardPath,
                        color: s.color,
                        life: 1.0,
                        maxLife: 1.0
                    })
                }
            })
        }

        // Render Shards
        data.shards.forEach((shard) => {
            // Physics
            shard.x += shard.vx
            shard.y += shard.vy
            shard.z += shard.vz // Move towards camera
            shard.angle += shard.rotationSpeed
            shard.life -= 0.015

            if (shard.life > 0) {
                // Perspective Projection
                // As Z increases (towards camera), scale increases
                const perspectiveScale = 1 + shard.z * 0.003

                // Shadow Logic
                // Shadow stays on ground (z=0), but fades and maybe gets slightly larger/blurrier
                ctx.save()
                ctx.translate(shard.x, shard.y + 150) // Ground level offset
                ctx.scale(perspectiveScale * 0.5, perspectiveScale * 0.15) // Flattened shadow
                ctx.fillStyle = 'black'
                ctx.globalAlpha = shard.life * 0.3 * (1 / (1 + shard.z * 0.01)) // Fade as it gets higher
                ctx.beginPath()
                ctx.arc(0, 0, 5, 0, Math.PI * 2)
                ctx.fill()
                ctx.restore()

                // Shard Logic
                ctx.save()
                ctx.translate(shard.x, shard.y) // X/Y are screen space coords, Z is scale
                ctx.scale(perspectiveScale, perspectiveScale)
                ctx.rotate(shard.angle)

                ctx.strokeStyle = shard.color
                ctx.lineWidth = 3
                ctx.lineCap = 'round'
                ctx.globalAlpha = shard.life

                // Add "motion blur" trail if moving fast
                if (shard.vz > 20) {
                    ctx.shadowColor = shard.color
                    ctx.shadowBlur = 10
                }

                ctx.stroke(shard.path)
                ctx.restore()
            }
        })
    }

    ctx.restore()
    return true
}
